import { useEffect, useRef } from 'react';
import { useEditor } from '../../state/editorStore';
import { api } from '../../api/client';

/**
 * Manages the preview person cut-out (the "behind the person" 3D depth) and its
 * opt-in dialog. Always mounted in the studio so it can:
 *  - hydrate the cut-out status when a project opens (so a cached cut-out is
 *    ready to use immediately),
 *  - poll while a generation runs, and
 *  - show the "generate this?" prompt when the user first turns a hook behind.
 *
 * The heavy matting runs ONCE on the server and is cached, so this is a one-time
 * wait per clip; afterwards the effect is instant on every open.
 */
export function StudioDepthDialog() {
  const projectId = useEditor((s) => s.projectId);
  const cutout = useEditor((s) => s.cutout);
  const setCutout = useEditor((s) => s.setCutout);
  const open = useEditor((s) => s.depthDialogOpen);
  const setOpen = useEditor((s) => s.setDepthDialogOpen);
  const pollRef = useRef<number | null>(null);

  const stopPoll = () => {
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = null;
  };

  // Own the cut-out lifecycle per project: clear stale state, then hydrate the
  // current project's status. (The editor store does NOT reset `cutout` on load
  // precisely so this fetch can't be clobbered by a concurrent transcript load.)
  useEffect(() => {
    setCutout({ status: 'idle', url: null, progress: 0, track: null });
    if (!projectId) return;
    let cancelled = false;
    api
      .getCutout(projectId)
      .then((c) => {
        if (cancelled) return;
        setCutout({ status: c.status, url: c.url, progress: c.progress, track: c.track ?? null });
        if (c.status === 'running') startPolling();
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      stopPoll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const startPolling = () => {
    stopPoll();
    pollRef.current = window.setInterval(() => {
      const pid = useEditor.getState().projectId;
      if (!pid) return stopPoll();
      api
        .getCutout(pid)
        .then((c) => {
          setCutout({ status: c.status, url: c.url, progress: c.progress, track: c.track ?? null });
          if (c.status !== 'running') stopPoll();
        })
        .catch(() => undefined);
    }, 2000);
  };

  const generate = async () => {
    if (!projectId) return;
    setCutout({ status: 'running', progress: 0 });
    try {
      await api.startCutout(projectId);
      startPolling();
    } catch {
      setCutout({ status: 'failed' });
    }
  };

  if (!open) return null;
  const pct = Math.round((cutout.progress ?? 0) * 100);
  const running = cutout.status === 'running';
  const ready = cutout.status === 'ready';

  return (
    <div className="depthdlg-scrim" onClick={() => !running && setOpen(false)}>
      <div className="depthdlg" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="depthdlg-icon" aria-hidden>◐</div>
        <h3>Behind the person (3D)</h3>
        <p>This needs a one-time processing pass on your clip. Run it once, then it’s cached.</p>

        {ready ? (
          <p className="depthdlg-ok">✓ Ready.</p>
        ) : running ? (
          <div className="depthdlg-progress">
            <div className="depthdlg-bar">
              <div className="depthdlg-fill" style={{ width: `${Math.max(6, pct)}%` }} />
            </div>
            <span>Processing… {pct}%</span>
          </div>
        ) : cutout.status === 'failed' ? (
          <p className="depthdlg-err">Processing failed. Try again.</p>
        ) : null}

        <div className="depthdlg-actions">
          {ready ? (
            <button className="depthdlg-primary" onClick={() => setOpen(false)}>Done</button>
          ) : (
            <>
              <button className="depthdlg-ghost" onClick={() => setOpen(false)} disabled={running}>
                {running ? 'Working…' : 'Not now'}
              </button>
              <button className="depthdlg-primary" onClick={generate} disabled={running || !projectId}>
                {cutout.status === 'failed' ? 'Try again' : 'Process'}
              </button>
            </>
          )}
        </div>
        {running && <p className="depthdlg-hint">You can keep editing - this runs in the background.</p>}
      </div>
    </div>
  );
}
